/**
 * CaseOfferPanel — the offer on a case.
 *
 * Phase 4 (G.12/G.13) gave this panel the drafted offer wording for staff to copy out. Lloyd,
 * Aug 18, asked for the offer to be entered and kept: "We need a way of inputting the offer
 * details, which once complete, then sends it to the client."
 *
 * So the panel now holds the record — enter the offer, review what the client will get, send it by
 * email and text — and keeps the court acceptance draft, which is still a copy-out tool because it
 * goes to a court rather than through this system.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OfferPortalApi, {
  DraftedEmail,
  OfferRecord,
  RecordOfferInput,
} from '../../api/OfferPortalApi';
import OfferRecordForm from './OfferRecordForm';
import OfferSendPanel from './OfferSendPanel';
import OfferSummary from './OfferSummary';

interface CaseOfferPanelProps {
  Ticket_guid: string;
}

function CaseOfferPanel({ Ticket_guid }: CaseOfferPanelProps) {
  const [offers, setOffers] = useState<OfferRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [courtDraft, setCourtDraft] = useState<DraftedEmail | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOffers(await OfferPortalApi.listOffers(Ticket_guid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the offers on this case');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    void load();
  }, [load]);

  const record = async (input: RecordOfferInput) => {
    setSaving(true);
    setError(null);
    try {
      const saved = await OfferPortalApi.recordOffer(Ticket_guid, input);
      setOffers((previous) => [saved, ...previous]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the offer');
    } finally {
      setSaving(false);
    }
  };

  /** Replace one offer in place after a send, rather than refetching the whole case's history. */
  const replaceOffer = (updated: OfferRecord) => {
    setOffers((previous) => previous.map((o) => (o.guid === updated.guid ? updated : o)));
  };

  const draftCourtAcceptance = async () => {
    setError(null);
    try {
      const type = offers[0]?.offerType ?? 'deferral';
      setCourtDraft(await OfferPortalApi.buildCourtAcceptance(Ticket_guid, type));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build the court acceptance draft');
    }
  };

  const copyCourtDraft = async () => {
    if (!courtDraft) return;
    try {
      await navigator.clipboard.writeText(`${courtDraft.subject}\n\n${courtDraft.body}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError('Could not copy.');
    }
  };

  const current = offers[0] ?? null;

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Offer
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading the offers on this case…
        </Typography>
      ) : current ? (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <OfferSummary offer={current} />
          {offers.slice(1).map((offer) => (
            <OfferSummary key={offer.guid} offer={offer} superseded />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No offer has been recorded on this case yet.
        </Typography>
      )}

      {current && (
        <>
          <OfferSendPanel offer={current} onSent={replaceOffer} />
          <Divider sx={{ my: 3 }} />
        </>
      )}

      <OfferRecordForm onSubmit={record} saving={saving} />

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Court acceptance draft
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        For when the client accepts. Drafted for you to review and send to the court.
      </Typography>
      <Button variant="outlined" onClick={draftCourtAcceptance}>
        Draft court acceptance
      </Button>

      {copied && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Court acceptance draft copied.
        </Alert>
      )}

      {courtDraft && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Court acceptance
            </Typography>
            <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copyCourtDraft}>
              Copy
            </Button>
          </Stack>
          <TextField
            label="Subject"
            value={courtDraft.subject}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            sx={{ mb: 1 }}
          />
          <TextField
            label="Body"
            value={courtDraft.body}
            fullWidth
            multiline
            minRows={8}
            InputProps={{ readOnly: true }}
          />
        </Box>
      )}
    </Box>
  );
}

export default CaseOfferPanel;
